import { useEffect, type ReactNode } from 'react';
import { PRODUCT_NAME } from '../BrandMark.tsx';
import { ensureGoogleFonts } from '../../lib/googleFonts.ts';
import {
  DRIVER_PANEL_EDGE,
  DRIVER_STEP,
  PANEL_ROW_WARN,
  SIGNIN_COLORS,
  SIGNIN_MONO,
  SIGNIN_PANELS,
  SIGNIN_SANS,
  SIGNIN_THEME,
  type SignInAudience,
} from './signInTheme.ts';

/**
 * The sign-in shell from the "Compliance Sign-in" design canvas: a role-coloured
 * panel beside the form column, collapsing to a compact bar above the form on a
 * phone.
 *
 * WHY A ROLE-SPECIFIC PANEL IS SAFE NOW AND WAS NOT BEFORE. Until the
 * per-audience hostnames landed (#92/#93, and `bb3619b` which serves only the
 * path family a host's audience owns), /c/login and /d/login were two paths on
 * one host, so a page could not treat its audience as a static fact. It can
 * now: `audience` is fixed per route, not sniffed.
 *
 * WHAT THIS DOES NOT TOUCH. The lockup is still `branding.logoUrl` before the
 * product mark, and the primary button still takes `var(--brand-accent)` — a
 * tenant's own logo and colour survive the redesign. Only the page ground, the
 * panel and the control styling are fixed. See signInTheme.ts.
 *
 * BREAKPOINT. 840px, as an arbitrary `min-[840px]:` rather than `md` (768) or
 * `lg` (1024) — at 768 the split is already cramped, and at 1024 a landscape
 * tablet loses a panel it has room for.
 *
 * NOT the canvas's 820, and the 20px matters. The two columns declare
 * `flex-basis` 360 and 480, which total 840 on a `flex-wrap: wrap` row: at any
 * width below that they cannot share a line and wrap onto separate ones. The
 * canvas has this bug — its own JS breakpoint is `width < 820` over the same two
 * bases — and rendering it faithfully reproduced it. Measured at 821x900 before
 * the change: panel heading at y=141, the form's <h1> at y=707 and the email
 * field at y=850, i.e. below the fold, with the panel stacked above it like a
 * very tall hero. Splitting at the width where both bases actually fit gives
 * 820-839 the compact bar instead, which is the right treatment for it anyway.
 */

/**
 * Load Archivo + IBM Plex Mono, once, from inside the component rather than
 * from each app's index.html — so the two families are fetched only on the
 * screens drawn from the sign-in canvas (the sign-in routes and the visitor
 * kiosk). See ensureGoogleFonts for the idempotency and `display=swap` notes.
 */
export function ensureSignInFonts(): void {
  ensureGoogleFonts(
    'l2u-signin-fonts',
    'https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap',
  );
}

/** The product lockup — inverted chip plus wordmark — as the panel renders it. */
function PanelLockup({ audience, size }: { audience: SignInAudience; size: 'bar' | 'panel' }) {
  const theme = SIGNIN_THEME[audience];
  const bar = size === 'bar';
  const box = bar ? 26 : 34;
  const dot = bar ? 9 : 11;
  return (
    <div className="flex items-center" style={{ gap: bar ? 9 : 11 }}>
      {/* Not <BrandMark>: the mark's fill is a fixed product green, which
          disappears on the near-black admin panel. The panel's lockup is the
          canvas's inverted chip — the product mark's geometry in reverse. The
          real <BrandMark> is still what the FORM column falls back to. */}
      <div
        className="flex flex-none items-center justify-center"
        style={{
          width: box,
          height: box,
          borderRadius: bar ? 7 : 9,
          background: theme.chipBg,
        }}
      >
        <div
          style={{ width: dot, height: dot, borderRadius: 3, background: theme.panelBg }}
          aria-hidden="true"
        />
      </div>
      <div
        style={{
          fontSize: bar ? 15 : 19,
          fontWeight: 700,
          letterSpacing: bar ? '-0.2px' : '-0.3px',
        }}
      >
        {PRODUCT_NAME}
      </div>
    </div>
  );
}

/** Mono, uppercase, wide-tracked — the canvas's eyebrow treatment. */
function Eyebrow({
  children,
  color,
  size = 11.5,
  tracking = 1.7,
}: {
  children: ReactNode;
  color: string;
  size?: number;
  tracking?: number;
}) {
  return (
    <div
      style={{
        fontFamily: SIGNIN_MONO,
        fontSize: size,
        letterSpacing: `${tracking}px`,
        textTransform: 'uppercase',
        color,
      }}
    >
      {children}
    </div>
  );
}

/** The full-height panel shown from 840px up. */
function Panel({ audience }: { audience: SignInAudience }) {
  const theme = SIGNIN_THEME[audience];
  const panel = SIGNIN_PANELS[audience];
  return (
    // `items-stretch` on the page root plus this wrapper is what makes the
    // panel full-bleed to the viewport height rather than card-height.
    <div className="hidden flex-[1_1_360px] min-[840px]:flex">
      <div
        className="flex min-h-[600px] flex-1 flex-col justify-between gap-11 p-11"
        style={{
          background: theme.panelBg,
          color: theme.panelFg,
          // Only the sand panel needs an edge: on paper it would otherwise have
          // no boundary at all. The two dark panels are their own boundary.
          borderRight: audience === 'driver' ? `1px solid ${DRIVER_PANEL_EDGE}` : undefined,
        }}
      >
        <PanelLockup audience={audience} size="panel" />

        <div className="flex max-w-[420px] flex-col gap-[26px]">
          <Eyebrow color={theme.panelMuted}>{panel.eyebrow}</Eyebrow>
          <h2
            className="m-0 font-bold"
            style={{ fontSize: 31, lineHeight: 1.14, letterSpacing: '-1px', textWrap: 'pretty' }}
          >
            {panel.heading}
          </h2>

          {panel.bullets && (
            <div className="flex flex-col gap-[13px]">
              {panel.bullets.map((text) => (
                <div key={text} className="flex items-start gap-3">
                  <div
                    className="mt-2 flex-none"
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 2,
                      background: theme.panelMuted,
                    }}
                    aria-hidden="true"
                  />
                  <div style={{ fontSize: 14.5, lineHeight: 1.55, color: theme.panelBody }}>
                    {text}
                  </div>
                </div>
              ))}
            </div>
          )}

          {panel.rows && (
            <div className="flex flex-col gap-2.5">
              {panel.rows.map((row) => (
                <div
                  key={row.label}
                  className="flex items-center justify-between gap-3.5 rounded-[9px] px-[15px] py-[13px]"
                  style={{ background: 'rgba(247,246,239,0.07)' }}
                >
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{row.label}</div>
                  <div
                    style={{
                      fontFamily: SIGNIN_MONO,
                      fontSize: 11,
                      letterSpacing: '0.8px',
                      color: row.tone === 'warn' ? PANEL_ROW_WARN : theme.panelMuted,
                    }}
                  >
                    {row.meta}
                  </div>
                </div>
              ))}
            </div>
          )}

          {panel.steps && (
            <div className="flex flex-col gap-3">
              {panel.steps.map((text, i) => (
                <div
                  key={text}
                  className="flex items-start gap-3.5 rounded-[11px] px-4 py-[15px]"
                  style={{ background: DRIVER_STEP.bg, border: `1px solid ${DRIVER_STEP.border}` }}
                >
                  <div
                    className="flex-none pt-px"
                    style={{ fontFamily: SIGNIN_MONO, fontSize: 12, color: DRIVER_STEP.num }}
                    aria-hidden="true"
                  >
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <div style={{ fontSize: 14.5, lineHeight: 1.5, color: theme.panelBody }}>
                    {text}
                  </div>
                </div>
              ))}
            </div>
          )}

          {panel.note && (
            <div
              style={{
                fontSize: 14,
                lineHeight: 1.55,
                color: theme.panelBody,
                textWrap: 'pretty',
              }}
            >
              {panel.note}
            </div>
          )}
        </div>

        {/* The canvas prints a hardcoded "© 2026 SUPPLIERCLEAR · V1.0.93". The
            year is computed and the version is left to <AppVersion/> in the form
            column, which reads the real VITE_APP_VERSION the pipeline bakes in —
            a made-up version number on the login page is a support trap. */}
        <div
          style={{
            fontFamily: SIGNIN_MONO,
            fontSize: 11,
            letterSpacing: '0.6px',
            color: theme.panelFoot,
          }}
        >
          © {new Date().getFullYear()} {PRODUCT_NAME.toUpperCase()}
        </div>
      </div>
    </div>
  );
}

/** The compact bar shown below 840px in place of the panel. */
function MobileBar({ audience }: { audience: SignInAudience }) {
  const theme = SIGNIN_THEME[audience];
  const panel = SIGNIN_PANELS[audience];
  return (
    <div
      className="flex flex-[1_1_100%] flex-col gap-3.5 px-5 py-4 min-[840px]:hidden"
      style={{
        background: theme.panelBg,
        color: theme.panelFg,
        borderBottom: audience === 'driver' ? `1px solid ${DRIVER_PANEL_EDGE}` : undefined,
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <PanelLockup audience={audience} size="bar" />
        <Eyebrow color={theme.panelMuted} size={10} tracking={1.3}>
          {panel.mobileBadge}
        </Eyebrow>
      </div>
      {panel.mobileTagline && (
        <div
          style={{
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: '-0.5px',
            lineHeight: 1.25,
            textWrap: 'pretty',
          }}
        >
          {panel.mobileTagline}
        </div>
      )}
    </div>
  );
}

export function SignInLayout({
  audience,
  className = '',
  children,
}: {
  audience: SignInAudience;
  /**
   * Extra classes on the page root. The portal passes `driver-portal` here,
   * which is what opts a driver's sign-in into index.css's phone touch-target
   * rules — those are descendant selectors, so the class has to be on an
   * ancestor of the controls rather than on the form.
   */
  className?: string;
  /** The form column. */
  children: ReactNode;
}) {
  useEffect(ensureSignInFonts, []);

  return (
    <div
      className={`flex min-h-screen flex-wrap items-stretch ${className}`}
      style={{
        background: SIGNIN_COLORS.paper,
        color: SIGNIN_COLORS.ink,
        fontFamily: SIGNIN_SANS,
      }}
    >
      <MobileBar audience={audience} />
      <Panel audience={audience} />
      {/* `flex-[1_1_480px]` against the panel's 360 is what makes the form the
          wider half at every width the two share a row. Their 840px of combined
          basis is also what sets the breakpoint — see the note at the top. */}
      <div className="flex flex-[1_1_480px] flex-col items-center justify-center gap-[26px] px-[22px] pb-16 pt-10">
        <div className="flex w-full max-w-[420px] flex-col gap-6">{children}</div>
      </div>
    </div>
  );
}

export { Eyebrow as SignInEyebrow };
